<template>
  <ModalRoot v-model="isOpen">
    <template #body="{ close }">
      <ModalBody
        :is-open="isOpen"
        hide-close-button
        class="!p-0 !bg-bg overflow-hidden w-full md:w-[500px] rounded-0 md:rounded-[35px] md:h-auto"
        @close="close"
      >
        <div
          class="p-6 bg-fill-brand-secondary backdrop-blur-[100px] flex flex-col gap-6 h-full md:h-auto"
        >
          <!-- Header -->
          <div class="h-10 justify-between items-center flex w-full">
            <h2 class="text-xl text-text font-serif">
              {{ $t("channel.leaderboard.channelActions") }}
            </h2>
            <div
              class="w-10 h-10 bg-fill-brand-secondary rounded-full backdrop-blur-[35px] flex justify-center items-center cursor-pointer"
              @click="close"
            >
              <Icon icon="mdi:close" class="size-6 text-white" />
            </div>
          </div>

          <!-- Content -->
          <div
            class="flex-col justify-start items-start gap-4 inline-flex overflow-y-auto hide-scrollbar"
          >
            <!-- Add New Button -->
            <button
              class="self-stretch h-10 px-6 py-1 bg-fill-brand-secondary rounded-lg justify-center items-center gap-1 inline-flex hover:bg-fill-brand-secondary-hover transition-colors"
              @click="addNewChannelAction"
            >
              <Icon icon="material-symbols:add" class="size-6 text-white" />
              <div
                class="text-center text-white text-sm font-medium leading-tight"
              >
                {{ $t("channel.leaderboard.addNewChannelAction") }}
              </div>
            </button>

            <!-- Channel Actions List -->
            <div class="flex flex-col gap-4 w-full max-h-[510px]">
              <ChannelActionsEditItem
                v-for="(channelAction, i) in localChannelActions"
                :key="channelAction.id"
                is-new
                :item="channelAction"
                :available-app-actions="availableAppActions"
                @saved="() => onChannelActionSaved(i, true)"
                @deleted="() => onChannelActionDeleted(i, true)"
              />

              <ChannelActionsEditItem
                v-for="(channelAction, i) in savedChannelActions"
                :key="channelAction.id"
                :item="channelAction"
                :is-new="false"
                :available-app-actions="availableAppActions"
                @saved="() => onChannelActionSaved(i, false)"
                @deleted="() => onChannelActionDeleted(i, false)"
              />
            </div>
          </div>
        </div>
      </ModalBody>
    </template>
  </ModalRoot>
</template>

<script lang="ts">
export interface LocalChannelAction {
  id?: string;
  title: string;
  short_title: string;
  notification_title?: string;
  app_action_name: string;
  app_action_title?: string;
  points?: number;
  is_logical?: boolean;
}
</script>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import ModalBody from "@/components/ui/modal/modal-body.vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ChannelActionsEditItem from "./channel-actions-edit-item.vue";
import { computed, reactive, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { graphql as gql } from "@/generated";

const GetChannelActionsDocument = gql(/* GraphQL */ `
  query GetChannelActions {
    getChannelActions {
      ... on ChannelActionForChannelAdminList {
        list {
          id
          title
          short_title
          notification_title
          app_action_name
          app_action_title
          points
          is_logical
        }
      }
    }
  }
`);

const GetAvailableAppActionsDocument = gql(/* GraphQL */ `
  query GetAvailableAppActions {
    getAvailableAppActions {
      ... on AvailableAppActionsList {
        list
      }
    }
  }
`);

const isOpen = defineModel<boolean>("isOpen", { required: true });

const { result: channelActionsResult, refetch: refetchChannelActions } =
  useQuery(GetChannelActionsDocument);

const { result: appActionsResult } = useQuery(GetAvailableAppActionsDocument);

const localChannelActions = reactive<LocalChannelAction[]>([]);

const savedChannelActions = computed(() => {
  const result = channelActionsResult.value?.getChannelActions;
  return result && "list" in result ? result.list : [];
});

const availableAppActions = computed(() => {
  const result = appActionsResult.value?.getAvailableAppActions;
  return result && "list" in result ? result.list : [];
});

function clearLocalChannelActions() {
  localChannelActions.splice(0, localChannelActions.length);
}

function addNewChannelAction() {
  localChannelActions.unshift({
    id: "new_" + Date.now(),
    title: "",
    short_title: "",
    notification_title: "",
    app_action_name: "",
  });
}

async function onChannelActionSaved(index: number, isNew: boolean) {
  await refetchChannelActions();

  if (isNew) {
    localChannelActions.splice(index, 1);
  }
}

async function onChannelActionDeleted(index: number, isNew: boolean) {
  if (isNew) {
    localChannelActions.splice(index, 1);
  } else {
    await refetchChannelActions();
  }
}

watch(isOpen, (value) => {
  if (!value) {
    clearLocalChannelActions();
  }
});
</script>
